import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import RoleTable from "@/components/tables/RoleTable";
import TableHead from "@/components/tables/TableHead";
import { fetchRoles } from "@/services/role.service";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { ALL_ROLES } from "@/utils/constants/constants";
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import { getListCacheBootstrap, isListFullyFetched, writeListCache } from "@/utils/listPageCache";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";

const RoleList = () => {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("roles", { dataLength, sorting });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [roles, setRoles] = useState<any>(listBootstrap.items);
    const [rolesLeft, setRolesLeft] = useState(true);
    const [allFetched, setAllFetched] = useState(false);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);
    const isCreateRolePermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalCreateRole);
    const loadMoreLockRef = useRef(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const { cacheKey, cached } = getListCacheBootstrap("roles", { dataLength, sorting });

        setPageNo(1);
        setAllFetched(false);
        setRolesLeft(true);
        loadMoreLockRef.current = false;
        if (cached) {
            setRoles(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
            if (isListFullyFetched(cached.items.length, cached.totalCount, dataLength as number)) {
                setAllFetched(true);
                setRolesLeft(false);
            }
        } else {
            setIsLoading(true);
            setDataLoad(true);
        }

        fetchRoles(
            1,
            dataLength,
            sorting,
            (response: any) => {
                const nextRoles = response.data.roles ?? [];
                const reportedTotal = response.data.total_count;
                writeListCache(cacheKey, nextRoles, reportedTotal);
                setDataLoad(false);
                setIsLoading(false);
                setTotalCount(reportedTotal);
                loadMoreLockRef.current = false;
                if (nextRoles.length > 0) {
                    setRoles(nextRoles);
                    storeInLocalStorage(ALL_ROLES, nextRoles);
                    const hasMore =
                        reportedTotal != null
                            ? nextRoles.length < reportedTotal
                            : nextRoles.length >= dataLength;
                    setRolesLeft(hasMore);
                    setAllFetched(!hasMore);
                } else {
                    setAllFetched(true);
                    setRolesLeft(false);
                }
            },
            (error: any) => {
                setDataLoad(false);
                console.log(error);
                setIsLoading(false);
                loadMoreLockRef.current = false;
            }
        );
    }, [dataLength, sorting]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && rolesLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;

            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchRoles(
                nextPage,
                dataLength,
                sorting,
                (response: any) => {
                    const nextRoles = response.data.roles ?? [];
                    const reportedTotal = response.data.total_count;

                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                    setTotalCount(reportedTotal);

                    if (nextRoles.length > 0) {
                        setRoles((prev: any[]) => {
                            const merged = [...prev, ...nextRoles];
                            storeInLocalStorage(ALL_ROLES, merged);
                            const hasMore =
                                reportedTotal != null
                                    ? merged.length < reportedTotal
                                    : nextRoles.length >= dataLength;
                            setRolesLeft(hasMore);
                            setAllFetched(!hasMore);
                            return merged;
                        });
                    } else {
                        setRolesLeft(false);
                        setAllFetched(true);
                    }
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                }
            );
        },
    });

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id='role-table'
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    title="Roles"
                    button={
                        isCreateRolePermitted && (
                            <Button asChild id='create-role-btn'>
                                <Link to="/role">
                                    {BUTTONS.CREATE}
                                </Link>
                            </Button>
                        )
                    }
                    currentEnvo={roles.length}
                    totalEnvo={totalCount}
                />
            </div>
            <div>
                <RoleTable id='role-table' setRoles={setRoles} isLoading={isLoading} dataLength={dataLength as number} sorting={sorting} setSorting={setSorting as ({}) => void} allFetched={allFetched} roles={roles} setPageNo={setPageNo} dataLoad={dataLoad} />
            </div>
        </div>
    );
};

export default RoleList;
